import React, { useState, useEffect, useRef } from 'react';

const FlappyBird = () => {
    // Game constants
    const GRAVITY = 0.6;
    const JUMP_STRENGTH = -10;
    const BIRD_SIZE = 40;
    const PIPE_WIDTH = 60;
    const PIPE_GAP = 180;
    const PIPE_SPEED = 3;
    const GAME_WIDTH = 400;
    const GAME_HEIGHT = 600;

    // Game state
    const [birdY, setBirdY] = useState(GAME_HEIGHT / 2);
    const [birdVelocity, setBirdVelocity] = useState(0);
    const [pipes, setPipes] = useState([]);
    const [score, setScore] = useState(0);
    const [gameStarted, setGameStarted] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const [highScore, setHighScore] = useState(0);

    const gameLoopRef = useRef();
    const pipeTimerRef = useRef();

    // Reset game
    const resetGame = React.useCallback(() => {
        setBirdY(GAME_HEIGHT / 2);
        setBirdVelocity(0);
        setPipes([]);
        setScore(0);
        setGameStarted(false);
        setGameOver(false);
    }, []);

    // Jump function
    const jump = React.useCallback(() => {
        if (!gameStarted) {
            setGameStarted(true);
            return;
        }
        if (gameOver) {
            resetGame();
            return;
        }
        setBirdVelocity(JUMP_STRENGTH);
    }, [gameStarted, gameOver, resetGame, JUMP_STRENGTH]);

    // Handle input
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.code === 'Space') {
                e.preventDefault();
                jump();
            }
        };

        const handleTouch = (e) => {
            e.preventDefault();
            jump();
        };

        window.addEventListener('keydown', handleKeyPress);
        window.addEventListener('touchstart', handleTouch);
        window.addEventListener('click', jump);

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
            window.removeEventListener('touchstart', handleTouch);
            window.removeEventListener('click', jump);
        };
    }, [gameStarted, gameOver, jump]);

    // Game loop
    useEffect(() => {
        if (!gameStarted || gameOver) return;

        gameLoopRef.current = setInterval(() => {
            // Update bird position
            setBirdY(prev => {
                const newY = prev + birdVelocity;
                // Check ground/ceiling collision
                if (newY <= 0 || newY >= GAME_HEIGHT - BIRD_SIZE) {
                    setGameOver(true);
                    if (score > highScore) setHighScore(score);
                    return prev;
                }
                return newY;
            });

            setBirdVelocity(prev => prev + GRAVITY);

            // Update pipes
            setPipes(prev => {
                const newPipes = prev
                    .map(pipe => ({ ...pipe, x: pipe.x - PIPE_SPEED }))
                    .filter(pipe => pipe.x > -PIPE_WIDTH);

                // Check collision with pipes
                newPipes.forEach(pipe => {
                    const birdLeft = GAME_WIDTH / 2 - BIRD_SIZE / 2;
                    const birdRight = GAME_WIDTH / 2 + BIRD_SIZE / 2;
                    const birdTop = birdY;
                    const birdBottom = birdY + BIRD_SIZE;

                    if (
                        birdRight > pipe.x &&
                        birdLeft < pipe.x + PIPE_WIDTH &&
                        (birdTop < pipe.topHeight || birdBottom > pipe.topHeight + PIPE_GAP)
                    ) {
                        setGameOver(true);
                        if (score > highScore) setHighScore(score);
                    }

                    // Score when passing pipe
                    if (!pipe.scored && pipe.x + PIPE_WIDTH < GAME_WIDTH / 2) {
                        pipe.scored = true;
                        setScore(s => s + 1);
                    }
                });

                return newPipes;
            });
        }, 1000 / 60); // 60 FPS

        return () => clearInterval(gameLoopRef.current);
    }, [gameStarted, gameOver, birdVelocity, birdY, score, highScore]);

    // Pipe generation
    useEffect(() => {
        if (!gameStarted || gameOver) return;

        pipeTimerRef.current = setInterval(() => {
            const minHeight = 50;
            const maxHeight = GAME_HEIGHT - PIPE_GAP - 50;
            const topHeight = Math.random() * (maxHeight - minHeight) + minHeight;

            setPipes(prev => [
                ...prev,
                {
                    x: GAME_WIDTH,
                    topHeight,
                    scored: false
                }
            ]);
        }, 2000); // New pipe every 2 seconds

        return () => clearInterval(pipeTimerRef.current);
    }, [gameStarted, gameOver]);

    return (
        <div className="flappy-container">
            <div className="flappy-game" style={{ width: GAME_WIDTH, height: GAME_HEIGHT }}>
                {/* Background */}
                <div className="flappy-bg"></div>

                {/* Bird */}
                <div
                    className="flappy-bird"
                    style={{
                        left: GAME_WIDTH / 2 - BIRD_SIZE / 2,
                        top: birdY,
                        width: BIRD_SIZE,
                        height: BIRD_SIZE,
                        transform: `rotate(${Math.min(Math.max(birdVelocity * 3, -30), 90)}deg)`
                    }}
                >
                    🤖
                </div>

                {/* Pipes */}
                {pipes.map((pipe, index) => (
                    <React.Fragment key={index}>
                        {/* Top pipe */}
                        <div
                            className="flappy-pipe"
                            style={{
                                left: pipe.x,
                                top: 0,
                                width: PIPE_WIDTH,
                                height: pipe.topHeight
                            }}
                        />
                        {/* Bottom pipe */}
                        <div
                            className="flappy-pipe"
                            style={{
                                left: pipe.x,
                                top: pipe.topHeight + PIPE_GAP,
                                width: PIPE_WIDTH,
                                height: GAME_HEIGHT - pipe.topHeight - PIPE_GAP
                            }}
                        />
                    </React.Fragment>
                ))}

                {/* Score */}
                <div className="flappy-score">
                    Score: {score}
                    {highScore > 0 && <div className="flappy-highscore">Best: {highScore}</div>}
                </div>

                {/* Start/Game Over screen */}
                {!gameStarted && !gameOver && (
                    <div className="flappy-overlay">
                        <h1>Flappy Robot</h1>
                        <p>Click, Tap, or Press Space to Start</p>
                    </div>
                )}

                {gameOver && (
                    <div className="flappy-overlay">
                        <h1>Game Over!</h1>
                        <p>Score: {score}</p>
                        {score === highScore && score > 0 && <p className="flappy-new-record">New Record! 🎉</p>}
                        <p>Click, Tap, or Press Space to Restart</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FlappyBird;
